import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

// 日報の相互フィードバック（ADR 0021）。
//  - PeerFeedbackComposer … 日報提出(ShiftEnd)で「今日一緒に入ったメンバーへ」書く
//  - FeedbackList         … 日報履歴(ReflectionHistory)で全件を読み、自分の1件だけ編集する
// どちらも実名・全員公開。1人が同じ相手の同じ日に書けるのは1件（再保存は自分の分の上書き）。

const btnSm = (bg, fg, bd) => ({
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${bd}`, background: bg,
  color: fg, fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer', minHeight: 34,
});

const taStyle = {
  width: '100%', minHeight: 72, resize: 'vertical', boxSizing: 'border-box',
  padding: '10px 12px', background: 'var(--field)',
  border: '1px solid var(--rule-strong)', borderRadius: 8,
  color: 'var(--ink)', fontSize: 14, outline: 'none', colorScheme: 'dark',
};

// 'YYYY-MM-DD HH:MM:SS' → 'M/D HH:MM'
const fmtAt = (s) => {
  const d = String(s || '');
  if (d.length < 16) return '';
  return `${+d.slice(5, 7)}/${+d.slice(8, 10)} ${d.slice(11, 16)}`;
};

const JOB_LABEL = { catcher: 'キャッチャー', closer: 'クローザー', both: '兼務' };

// 対象メンバーの出し方（basis）を現場向けの日本語にする。
// ブースは実運用で未設定の日が多く、推定で出していることを隠さない（ADR 0021）。
function basisNote(basis, boothName) {
  if (basis === 'booth') return `${boothName || 'このブース'} に入ったメンバーです`;
  if (basis === 'memo_booth') return `ブース未選択のため、着座メモから ${boothName || 'ブース'} と推定しました`;
  return 'ブースが分からないため、今日入力があったメンバーを表示しています';
}

// ---- 1人ぶんの入力カード（提出画面用）----
function TargetCard({ member, date, onSaved }) {
  const [text, setText] = useState(member.my_feedback || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(member.my_feedback ? '保存済み' : '');

  // 保存済みの本文と一致していれば「未保存」バッジを出さない
  const dirty = text.trim() !== String(member.my_feedback || '').trim();

  const save = async () => {
    setSaving(true);
    try {
      await api.savePeerFeedback(member.user_id, date, text);
      setSavedAt(text.trim() ? '保存済み' : '削除しました');
      onSaved(member.user_id, text);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ padding: '10px 12px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink)' }}>{member.name}</span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{JOB_LABEL[member.job_type] || ''}</span>
        {dirty
          ? <span style={{ marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--hold)' }}>未保存</span>
          : savedAt ? <span style={{ marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--gain)' }}>{savedAt}</span> : null}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`${member.name}さんへ。今日良かったこと・次に活かせることを一言`}
        style={taStyle}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={save} disabled={saving || !dirty} style={{
          ...btnSm(dirty ? 'var(--mark)' : 'var(--paper-sunk)', dirty ? '#fff' : 'var(--ink-mute)', 'transparent'),
          cursor: (saving || !dirty) ? 'default' : 'pointer',
        }}>
          {saving ? '保存中…' : '保存する'}
        </button>
      </div>
    </div>
  );
}

// ---- 日報提出(ShiftEnd)に置く「今日一緒に入ったメンバーへ」----
// 保存は日報の保存ボタンとは独立させる（日報保存の失敗や画面遷移で下書きが消えるのを防ぐ）。
export function PeerFeedbackComposer({ date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOthers, setShowOthers] = useState(false);
  const [added, setAdded] = useState([]); // 手で追加したメンバー

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    api.getFeedbackTargets(date)
      .then((d) => { setData(d); setError(''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (userId, text) => {
    setData((prev) => {
      if (!prev) return prev;
      const patch = (m) => (m.user_id === userId ? { ...m, my_feedback: text } : m);
      return { ...prev, members: prev.members.map(patch), others: prev.others.map(patch) };
    });
    setAdded((prev) => prev.map((m) => (m.user_id === userId ? { ...m, my_feedback: text } : m)));
  };

  if (loading) return <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>読み込み中…</div>;
  if (error) return <div style={{ fontSize: 'var(--t-note)', color: 'var(--loss)' }}>{error}</div>;
  if (!data) return null;

  const addedIds = new Set(added.map((m) => m.user_id));
  const shown = [...data.members, ...added];
  const others = data.others.filter((m) => !addedIds.has(m.user_id));

  return (
    <div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 10 }}>
        {basisNote(data.basis, data.booth_name)}・書いた内容は実名で本人と全員に見えます
      </div>

      {shown.length === 0 && (
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 10 }}>
          今日の相手が見つかりませんでした。下の「他のメンバーを選ぶ」から選べます。
        </div>
      )}

      {shown.map((m) => (
        <TargetCard key={m.user_id} member={m} date={date} onSaved={onSaved} />
      ))}

      {others.length > 0 && (
        showOthers ? (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginBottom: 6 }}>追加する人を選ぶ</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {others.map((m) => (
                <button
                  key={m.user_id}
                  onClick={() => { setAdded((prev) => [...prev, m]); }}
                  style={btnSm('var(--paper-sunk)', 'var(--ink)', 'var(--ink-mute)')}
                >＋ {m.name}</button>
              ))}
            </div>
            <button onClick={() => setShowOthers(false)} style={{ ...btnSm('transparent', 'var(--ink-mute)', 'transparent'), marginTop: 8, paddingLeft: 0 }}>閉じる</button>
          </div>
        ) : (
          <button onClick={() => setShowOthers(true)} style={btnSm('var(--paper-sunk)', 'var(--ink-soft)', 'var(--ink-mute)')}>
            ＋ 他のメンバーを選ぶ
          </button>
        )
      )}
    </div>
  );
}

// ---- 日報履歴に置く「フィードバック一覧」----
// 全件を実名で読み、自分が書いた1件だけ編集・削除できる（管理者はどれでも削除できる）。
export function FeedbackList({ reflection, currentUser, onChanged }) {
  const list = reflection.feedbacks || [];
  const mine = list.find((f) => f.author_user_id === currentUser.id) || null;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(mine ? mine.body : '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setText(mine ? mine.body : ''); }, [mine, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await api.savePeerFeedback(reflection.user_id, reflection.reflection_date, text);
      setEditing(false);
      onChanged();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const remove = async (f) => {
    if (!window.confirm('このフィードバックを削除しますか？')) return;
    try {
      await api.deleteFeedback(f.id);
      onChanged();
    } catch (e) { alert(e.message); }
  };

  const canDelete = (f) => currentUser.role === 'admin' || f.author_user_id === currentUser.id;

  return (
    <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--mark-soft)', border: '1px solid #33406E', borderRadius: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--mark)' }}>フィードバック</span>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{list.length}件</span>
        {!editing && (
          <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} style={{ ...btnSm('var(--mark)', 'var(--mark)', 'var(--mark)'), marginLeft: 'auto' }}>
            {mine ? '自分の分を編集' : '＋ 追加'}
          </button>
        )}
      </div>

      {list.length === 0 && !editing && (
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>まだフィードバックはありません。</div>
      )}

      {list.map((f) => (
        <div key={f.id} style={{ paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid var(--rule-strong)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: f.author_user_id === currentUser.id ? 'var(--mark)' : 'var(--ink-soft)' }}>
              {f.author_name || '不明'}{f.author_user_id === currentUser.id ? '（あなた）' : ''}
            </span>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{fmtAt(f.updated_at)}</span>
            {canDelete(f) && (
              <button onClick={(e) => { e.stopPropagation(); remove(f); }} style={{ ...btnSm('transparent', 'var(--ink-mute)', 'transparent'), marginLeft: 'auto', padding: '2px 6px', minHeight: 0, fontSize: 'var(--t-note)' }}>削除</button>
            )}
          </div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{f.body}</div>
        </div>
      ))}

      {editing && (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            placeholder="日報とKPIを踏まえた一言（実名で本人と全員に見えます）"
            style={{ ...taStyle, minHeight: 84 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={save} disabled={saving} style={btnSm('var(--mark)', '#fff', 'transparent')}>
              {saving ? '保存中…' : '保存する'}
            </button>
            <button onClick={() => { setText(mine ? mine.body : ''); setEditing(false); }} style={btnSm('var(--paper-sunk)', 'var(--ink-soft)', 'transparent')}>キャンセル</button>
          </div>
        </>
      )}
    </div>
  );
}
